import {
  getSidekickActingOrganizationScopeHeaders,
  type SidekickActingOrganizationScope,
} from './actingOrganization';
import type {
  ConversationServiceShareApiResponse,
  CreateConversationRequest,
  CreateConversationResponse,
  DeleteConversationResponse,
  ListConversationMessagesResponse,
  ShareRecipients,
  ShareRequest,
  StopConversationResponse,
} from './generated-api';
import { sidekickApi } from './generated-api';

/**
 * Recipients as the client sends them. The authorization proxy reads
 * each user's address from their profile before the API sees the
 * share, then forwards only `emails`, which is all the API spec
 * describes.
 */
export type ProxiedShareRecipients = ShareRecipients & {
  /** Directory users, resolved and checked against their profiles. */
  userIds?: string[];
};

/** `ShareRequest` with recipients in the shape the proxy accepts. */
export type ProxiedShareRequest = Omit<ShareRequest, 'recipients'> & {
  recipients: ProxiedShareRecipients;
};

const LIST_TAG_ID = 'LIST';
const messagesTagId = (conversationId: string): string =>
  `MESSAGES:${conversationId}`;

export const sidekickRtkClient = sidekickApi
  .enhanceEndpoints({
    endpoints: {
      conversationServiceListConversations: {
        providesTags: (result) => [
          { type: 'ConversationService', id: LIST_TAG_ID },
          // Summary message counts and timestamps change whenever a message is sent.
          ...(result?.conversations?.flatMap(({ conversationId }) =>
            conversationId
              ? [{ type: 'ConversationService' as const, id: conversationId }]
              : []
          ) ?? []),
        ],
      },
      conversationServiceCreateConversation: {
        invalidatesTags: [{ type: 'ConversationService', id: LIST_TAG_ID }],
      },
      conversationServiceGetConversation: {
        providesTags: (_result, _error, { conversationId }) => [
          { type: 'ConversationService', id: conversationId },
        ],
      },
      conversationServiceDeleteConversation: {
        invalidatesTags: (_result, _error, { conversationId }) => [
          { type: 'ConversationService', id: LIST_TAG_ID },
          { type: 'ConversationService', id: conversationId },
          { type: 'ConversationService', id: messagesTagId(conversationId) },
        ],
      },
      conversationServiceListConversationMessages: {
        providesTags: (_result, _error, { conversationId }) => [
          { type: 'ConversationService', id: messagesTagId(conversationId) },
        ],
      },
      conversationServiceSendMessage: {
        invalidatesTags: (_result, _error, { conversationId }) => [
          { type: 'ConversationService', id: LIST_TAG_ID },
          { type: 'ConversationService', id: conversationId },
          { type: 'ConversationService', id: messagesTagId(conversationId) },
        ],
      },
      // A share only sends an email; no conversation, list, or message
      // cache it could leave stale.
      conversationServiceShare: {
        invalidatesTags: [],
      },
    },
  })
  .injectEndpoints({
    endpoints: (build) => ({
      conversationServiceShareRecipients: build.mutation<
        ConversationServiceShareApiResponse,
        { shareRequest: ProxiedShareRequest }
      >({
        query: ({ shareRequest }) => ({
          url: '/share',
          method: 'POST',
          body: shareRequest,
        }),
        // A share only sends an email; no conversation, list, or message
        // cache it could leave stale.
        invalidatesTags: [],
      }),
      conversationServiceCreateScopedConversation: build.mutation<
        CreateConversationResponse,
        {
          createConversationRequest: CreateConversationRequest;
          organizationScope: SidekickActingOrganizationScope;
        }
      >({
        query: ({ createConversationRequest, organizationScope }) => ({
          url: '/conversations',
          method: 'POST',
          body: createConversationRequest,
          headers: getSidekickActingOrganizationScopeHeaders(organizationScope),
        }),
        invalidatesTags: [{ type: 'ConversationService', id: LIST_TAG_ID }],
      }),
      conversationServiceDiscardConversation: build.mutation<
        DeleteConversationResponse,
        {
          conversationId: string;
          organizationScope: SidekickActingOrganizationScope;
        }
      >({
        query: ({ conversationId, organizationScope }) => ({
          url: `/conversations/${conversationId}`,
          method: 'DELETE',
          headers: getSidekickActingOrganizationScopeHeaders(organizationScope),
        }),
        invalidatesTags: (_result, _error, { conversationId }) => [
          { type: 'ConversationService', id: LIST_TAG_ID },
          { type: 'ConversationService', id: conversationId },
          { type: 'ConversationService', id: messagesTagId(conversationId) },
        ],
      }),
      // Read for the organization the lookup started in, on every page, even
      // if the user switches organizations while it reads.
      conversationServiceListScopedConversationMessages: build.query<
        ListConversationMessagesResponse,
        {
          conversationId: string;
          pageSize?: number;
          pageToken?: string;
          organizationScope: SidekickActingOrganizationScope;
        }
      >({
        query: ({
          conversationId,
          pageSize,
          pageToken,
          organizationScope,
        }) => ({
          url: `/conversations/${conversationId}/messages`,
          params: { pageSize, pageToken },
          headers: getSidekickActingOrganizationScopeHeaders(organizationScope),
        }),
        providesTags: (_result, _error, { conversationId }) => [
          { type: 'ConversationService', id: messagesTagId(conversationId) },
        ],
      }),
      // Sent for the organization the stop was clicked in, even if the user
      // switches organizations while the stop waits for the question.
      conversationServiceStopScopedConversation: build.mutation<
        StopConversationResponse,
        {
          conversationId: string;
          organizationScope: SidekickActingOrganizationScope;
        }
      >({
        query: ({ conversationId, organizationScope }) => ({
          url: `/conversations/${conversationId}/stop`,
          method: 'POST',
          headers: getSidekickActingOrganizationScopeHeaders(organizationScope),
        }),
        invalidatesTags: (_result, _error, { conversationId }) => [
          { type: 'ConversationService', id: LIST_TAG_ID },
          { type: 'ConversationService', id: conversationId },
          { type: 'ConversationService', id: messagesTagId(conversationId) },
        ],
      }),
    }),
  });

export const {
  useConversationServiceCreateScopedConversationMutation,
  useConversationServiceDiscardConversationMutation,
  useConversationServiceShareRecipientsMutation,
  useConversationServiceStopScopedConversationMutation,
  useLazyConversationServiceListConversationMessagesQuery,
} = sidekickRtkClient;
